import { ARENA, OBSTACLES, DEFAULT_CLASS_ID, TICK_DT, getClass, clamp, moveToward, type TankClass } from "@tank/shared";

// How many pending (unacknowledged) inputs we'll ever hold. Safety valve only —
// in practice the ack round-trip is a couple of ticks' worth at most.
const MAX_PENDING_INPUTS = 120;

/**
 * Client-side prediction + reconciliation for the LOCAL tank, using
 * deterministic REPLAY (the standard FPS-netcode technique) instead of ad-hoc
 * error blending:
 *
 *  1. Every fixed simulation tick (TICK_DT, matching the server's tick rate)
 *     we integrate the current input with `stepInput()`, which both moves the
 *     tank and records the input in a `pending` queue keyed by its seq.
 *  2. When a server snapshot arrives with its authoritative position and the
 *     last input seq it has processed, `applyServer()` resets our state to
 *     that authoritative baseline, drops the now-confirmed pending inputs, and
 *     REPLAYS the still-unacknowledged ones (same fixed dt, same integrator)
 *     to reconstruct an up-to-date predicted position.
 *
 * Because the server (ArenaRoom) now consumes exactly one queued input per
 * tick, one client input == one server tick, so this replay is exact modulo
 * the small approximation in our client-side collision shapes (circle vs the
 * server's Rapier box colliders) — no more heuristic "snap once drift exceeds
 * X" that caused visible teleports mid-drive.
 */
export class LocalTankController {
  x = 0;
  z = 0;
  rotY = 0;
  turretAngle = 0;
  seq = 0;
  initialized = false;
  cls: TankClass = getClass(DEFAULT_CLASS_ID);
  // Current ramped speed (m/s, signed along facing) — NOT part of the synced
  // TankState, so applyServer() deliberately never resets it: it's a soft,
  // self-correcting quantity that depends only on the (already-known) input
  // history, unlike x/z/rotY which must snap to the authoritative baseline.
  private currentSpeed = 0;

  private pending: Array<{ seq: number; fwd: number; turn: number; speedMult: number }> = [];

  setClass(id: string | undefined): void {
    const next = getClass(id);
    if (next.id !== this.cls.id) { this.pending.length = 0; this.currentSpeed = 0; }
    this.cls = next;
  }

  init(x: number, z: number, rotY: number): void {
    this.x = x;
    this.z = z;
    this.rotY = rotY;
    this.initialized = true;
    this.pending.length = 0;
    this.currentSpeed = 0;
  }

  /** Pure integrator: advances (x, z, rotY) by one step. Same math as the server's Tank.applyInput. */
  predict(fwd: number, turn: number, dt: number, speedMult = 1): void {
    this.rotY += turn * this.cls.turnSpeed * dt;
    const target = fwd * this.cls.maxSpeed * speedMult;
    this.currentSpeed = moveToward(this.currentSpeed, target, this.cls.accel * dt);
    this.x += Math.sin(this.rotY) * this.currentSpeed * dt;
    this.z += Math.cos(this.rotY) * this.currentSpeed * dt;
    // Client-side collision (walls + obstacles) so the local tank feels solid
    // and doesn't rubber-band. Server (Rapier) remains authoritative.
    const half = ARENA.HALF - 1;
    this.x = clamp(this.x, -half, half);
    this.z = clamp(this.z, -half, half);
    this.collide();
  }

  /**
   * Advance one fixed simulation tick with this input AND remember it (keyed
   * by seq) so a later server ack can replay from its authoritative baseline.
   * Call this once per TICK_DT, in lockstep with what's sent over the network.
   */
  stepInput(seq: number, fwd: number, turn: number, speedMult = 1): void {
    this.predict(fwd, turn, TICK_DT, speedMult);
    this.pending.push({ seq, fwd, turn, speedMult });
    if (this.pending.length > MAX_PENDING_INPUTS) this.pending.shift();
  }

  /** Resolve the tank (circle of cls.radius) against obstacle AABBs. */
  private collide(): void {
    const r = this.cls.radius;
    for (const o of OBSTACLES) {
      const minX = o.x - o.w / 2, maxX = o.x + o.w / 2;
      const minZ = o.z - o.d / 2, maxZ = o.z + o.d / 2;
      const cx = clamp(this.x, minX, maxX);
      const cz = clamp(this.z, minZ, maxZ);
      const dx = this.x - cx;
      const dz = this.z - cz;
      const d2 = dx * dx + dz * dz;
      if (d2 >= r * r) continue;
      if (d2 > 0.000001) {
        const d = Math.sqrt(d2);
        const push = r - d;
        this.x += (dx / d) * push;
        this.z += (dz / d) * push;
      } else {
        const dl = this.x - minX, dr = maxX - this.x, dt = this.z - minZ, db = maxZ - this.z;
        const m = Math.min(dl, dr, dt, db);
        if (m === dl) this.x = minX - r;
        else if (m === dr) this.x = maxX + r;
        else if (m === dt) this.z = minZ - r;
        else this.z = maxZ + r;
      }
    }
  }

  /**
   * Reconcile against a server snapshot: snap to the authoritative state, drop
   * acknowledged inputs, and replay whatever's still pending. Deterministic —
   * no drift threshold, no partial correction, no visible teleport (replayed
   * ticks are typically 1-2, i.e. a few centimeters at most).
   */
  applyServer(serverX: number, serverZ: number, serverRotY: number, lastSeq: number): void {
    this.pending = this.pending.filter((p) => p.seq > lastSeq);
    this.x = serverX;
    this.z = serverZ;
    this.rotY = serverRotY;
    for (const p of this.pending) {
      this.predict(p.fwd, p.turn, TICK_DT, p.speedMult);
    }
  }
}

